import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';

const RFQEmailsTab = memo(({ 
    rfqEmailData, 
    setRfqEmailData 
}) => {
    // State declarations
    const [isSendingAll, setIsSendingAll] = useState(false);
    const [sendingEmails, setSendingEmails] = useState([]);
    const [sentEmailIndices, setSentEmailIndices] = useState([]);
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const [deletingEmails, setDeletingEmails] = useState([]);
    const [deletedEmailIndices, setDeletedEmailIndices] = useState([]);

    // Handlers
    const handleEmailChanges = useCallback((index, updatedFields) => {
        setRfqEmailData(currentData => 
            currentData.map((email, idx) => 
                idx === index ? { ...email, ...updatedFields } : email
            )
        );
    }, [setRfqEmailData]);

    const deleteEmail = useCallback(async (index) => {
        setDeletingEmails(prev => [...prev, index]);
        await new Promise(resolve => setTimeout(resolve, 500));
        setRfqEmailData(currentData => currentData.filter((_, idx) => idx !== index));
        setDeletingEmails(prev => prev.filter(idx => idx !== index));
        setDeletedEmailIndices(prev => [...prev, index]);
        setTimeout(() => {
            setDeletedEmailIndices(prev => prev.filter(idx => idx !== index));
        }, 3000);    
    }, [setRfqEmailData]);

    const deleteAllEmails = useCallback(async () => {
        setIsDeletingAll(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        setRfqEmailData([]);
        setIsDeletingAll(false);
    }, [setRfqEmailData]);

    // Reset states when component unmounts or when email data changes
    useEffect(() => {
        if (rfqEmailData.length > 0) {
            setDeletedEmailIndices([]);
            setSentEmailIndices([]);
        }
        return () => {
            setDeletedEmailIndices([]);
            setSentEmailIndices([]);
            setSendingEmails([]);
            setIsSendingAll(false);
            setIsDeletingAll(false);
        };
    }, [rfqEmailData.length]);

    // Memoize email count calculation
    const emailCount = useMemo(() => rfqEmailData?.length ?? 0, [rfqEmailData]);

    // Separate the API call from the state management
    const sendEmailToAPI = useCallback(async (email) => {
        const response = await fetch('https://us-central1-maia-tech.cloudfunctions.net/sendRFQEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(email),
        });
        return response.json();
    }, []);

    // Single email send
    const sendRFQEmail = useCallback(async (email, index) => {   
        if (sendingEmails.includes(index) || sentEmailIndices.includes(index)) {
            console.log('Preventing duplicate send for index:', index);
            return;
        }
    
        setSendingEmails((prev) => [...prev, index]);
    
        try {
            await sendEmailToAPI(email);
            console.log(`Email ${email.rfq_id} (index: ${index}) sent successfully`);
            setSentEmailIndices((prev) => [...prev, index]);
            setTimeout(() => {
                setRfqEmailData((currentData) => currentData.filter((_, idx) => idx !== index));
            }, 3000);
        } catch (error) {
            console.error('Error sending email:', error);
        } finally {
            setSendingEmails((prev) => prev.filter((idx) => idx !== index));
        }
    }, [sendingEmails, sentEmailIndices, sendEmailToAPI, setRfqEmailData]);

    // Send all emails
    const sendAllEmails = useCallback(async () => {
        if (isSendingAll) return;
        setIsSendingAll(true);
    
        try {
            const successfulIndices = [];
    
            for (let index = 0; index < rfqEmailData.length; index++) {
                const email = rfqEmailData[index];
                try {
                    setSendingEmails((prev) => [...prev, index]);
                    setSentEmailIndices((prev) => [...prev, index]);
                    
                    // Add a small delay between sends
                    if (index > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                    
                    await sendEmailToAPI(email);
                    successfulIndices.push(index);
                } catch (error) {
                    console.error(`Failed to send email at index ${index}:`, error);
                    setSentEmailIndices(prev => prev.filter(idx => idx !== index));
                } finally {
                    setSendingEmails(prev => prev.filter(idx => idx !== index));
                }
            }
    
            // Remove all successfully sent emails after a delay
            if (successfulIndices.length > 0) {
                setTimeout(() => {
                    setRfqEmailData((prev) => prev.filter((_, idx) => !successfulIndices.includes(idx)));
                    setSentEmailIndices([]); // Clear sent indices after emails are removed
                }, 3000);
            }
        } catch (error) {
            console.error('Error during Send All operation:', error);
        } finally {
            setIsSendingAll(false);
        }
    }, [isSendingAll, rfqEmailData, sendEmailToAPI, setRfqEmailData]);

    // Add this handler back
    const handleEmailContentChange = useCallback((index, newContent) => {
        handleEmailChanges(index, { content: newContent });
    }, [handleEmailChanges]);

    // Add back the memoized email list
    const emailList = useMemo(() => {
        if (!rfqEmailData || rfqEmailData.length === 0) {
            return <div>No RFQ emails generated at this time.</div>;
        }
        
        return rfqEmailData.map((email, index) => (
            <div key={`${email.rfq_id}_${index}`} className="email-container">
                {sentEmailIndices.includes(index) ? (
                    <div className="success-message">Email sent successfully!</div>
                ) : deletedEmailIndices.includes(index) ? (
                    <div className="success-message">Email successfully deleted!</div>                        
                ) : (
                    <>
                        <input
                            type="email"
                            value={email.to}
                            onChange={(e) => handleEmailChanges(index, { ...email, to: e.target.value })}
                            placeholder="Recipient's email"
                            className="rfq-email-to-input"
                        />
                        <div className="vendor-name">
                            Supplier: {email.supplier_name}
                            <span className="rfq-id">RFQ ID: {email.rfq_id}</span>
                        </div>
                        <textarea
                            className="rfq-email-textarea"
                            value={email.content}
                            onChange={(e) => handleEmailContentChange(index, e.target.value)}
                        />
                        <button 
                            onClick={async () => {
                                if (email.content === email.originalContent) {
                                    sendRFQEmail(email, index);
                                } else {
                                    const content = email.content;
                                    const mpnMatch = content.match(/Part Number: (.*?)(?:\n|$)/);
                                    const mfrMatch = content.match(/Manufacturer: (.*?)(?:\n|$)/);
                                    const qtyMatch = content.match(/Quantity: (.*?)(?:\n|$)/);

                                    const updatedEmail = {
                                        ...email,
                                        customer_mpn: mpnMatch ? mpnMatch[1].trim() : email.customer_mpn,
                                        customer_mfr: mfrMatch ? mfrMatch[1].trim() : email.customer_mfr,
                                        demand_qty: qtyMatch ? Number(qtyMatch[1].trim().replace(/,/g, '')) : email.demand_qty
                                    };

                                    sendRFQEmail(updatedEmail, index);
                                }
                            }} 
                            className="send-email-button" 
                            disabled={sendingEmails.includes(index)}
                        >
                            {sendingEmails.includes(index) ? 'Sending...' : 'Send Email'}
                        </button>
                        <button 
                            onClick={() => deleteEmail(index)}
                            className="delete-email-button" 
                            disabled={sendingEmails.includes(index) || deletingEmails.includes(index)}
                        >
                            {deletingEmails.includes(index) ? 'Deleting...' : 'Delete Email'}
                        </button>
                    </>
                )}
            </div>
        ));
    }, [rfqEmailData, sentEmailIndices, deletedEmailIndices, handleEmailChanges, handleEmailContentChange, sendRFQEmail, sendingEmails, deleteEmail, deletingEmails]);

    // Rest of your component code...
    return (
        <div className="rfq-emails-tab">
            <div className="top-buttons-container">
                <h3>RFQ Emails ({emailCount} Generated)</h3>
                <div className="buttons">
                    <button 
                        onClick={sendAllEmails}               
                        className="send-all-button" 
                        disabled={isSendingAll || emailCount === 0}
                    >
                        {isSendingAll ? 'Sending All...' : 'Send All'}
                    </button>
                    <button 
                        onClick={deleteAllEmails} 
                        className="delete-all-button" 
                        disabled={isDeletingAll || isSendingAll}
                    >
                        {isDeletingAll ? 'Deleting All...' : 'Delete All'}
                    </button>
                </div>
            </div>
            {emailList}
        </div>
    );
});

RFQEmailsTab.displayName = 'RFQEmailsTab';

export default RFQEmailsTab;